(() => {
  // SRL-55: below-fold img/video fade+translateY. Opt-out: data-srl-reveal="off" or .srl-reveal-skip.
  const PENDING_CLASS = 'srl-reveal';
  const VISIBLE_CLASS = 'srl-reveal-in';
  const MEDIA_SELECTOR = 'img, video';
  const GROUP_SELECTOR = '.swiper-container, .swiper, scroll-carousel, .product-gallery__carousel';
  const MIN_SIZE = 48;
  const ROOT_MARGIN = '0px 0px -4% 0px';
  const THRESHOLD = 0.08;
  const GROUP_STAGGER_MS = 60;

  const SKIP_CLOSEST = [
    '[data-srl-reveal="off"]',
    '.srl-reveal-skip',
    '[reveal-on-scroll]',
    'product-card',
    '[data-aw-cats]',
    '.aw-cats__item',
    '[data-aw-all]',
    '[data-aw-look]',
    '[data-aw-hero]',
    '.aw-lp',
    'header',
    '#section-featured',
    '#section-styling',
    '#section-officialapp',
    '.basic-hero',
    '.lulla-hero',
    '.mothersday-hero',
    'slideshow-carousel',
    '.collection-banner',
    'noscript',
    'template',
  ].join(',');

  const SKIP_SELF = [
    '.product-card__image--secondary',
    '.header__logo-image',
    '.pswp__img',
  ].join(',');

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const tracked = new WeakSet();
  const trackedGroups = new WeakSet();
  const revealedGroups = new WeakSet();
  const groupItems = new WeakMap();

  function prefersReducedMotion() {
    return motionQuery.matches;
  }

  function isDesignMode() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  function isHidden(el) {
    if (el.closest('[hidden], template, noscript')) {
      return true;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }
    return !el.offsetParent && style.position !== 'fixed';
  }

  function isTiny(el) {
    const attrW = Number(el.getAttribute('width')) || 0;
    const attrH = Number(el.getAttribute('height')) || 0;
    if ((attrW > 0 && attrW < MIN_SIZE) || (attrH > 0 && attrH < MIN_SIZE)) {
      return true;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && (rect.width < MIN_SIZE || rect.height < MIN_SIZE)) {
      return true;
    }
    return false;
  }

  function isInFirstViewport(el) {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return true;
    }
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < viewportHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
  }

  function collectMedia(root) {
    const scope = root instanceof Element && root.matches(MEDIA_SELECTOR) ? [root] : [];
    const nested = root.querySelectorAll ? [...root.querySelectorAll(MEDIA_SELECTOR)] : [];
    return [...scope, ...nested];
  }

  function collectGroups(root) {
    const scope = root instanceof Element && root.matches(GROUP_SELECTOR) ? [root] : [];
    const nested = root.querySelectorAll ? [...root.querySelectorAll(GROUP_SELECTOR)] : [];
    const groups = [...scope, ...nested];
    return groups.filter((group) => !groups.some((other) => other !== group && other.contains(group)));
  }

  function shouldSkip(el) {
    if (!(el instanceof HTMLElement)) {
      return true;
    }
    if (tracked.has(el) || el.classList.contains(VISIBLE_CLASS)) {
      return true;
    }
    if (el.matches(SKIP_SELF) || el.closest(SKIP_CLOSEST)) {
      return true;
    }
    if (el.getAttribute('fetchpriority') === 'high') {
      return true;
    }
    if (el.getAttribute('width') === '1' && el.getAttribute('height') === '1') {
      return true;
    }
    if (isHidden(el) || isTiny(el)) {
      return true;
    }
    return false;
  }

  function reveal(el) {
    el.style.willChange = 'opacity, transform';
    el.classList.add(VISIBLE_CLASS);
    const clearWillChange = () => {
      el.style.willChange = '';
    };
    el.addEventListener('transitionend', clearWillChange, { once: true });
    window.setTimeout(clearWillChange, 700);
  }

  function revealGroup(container) {
    const items = groupItems.get(container) || [];
    revealedGroups.add(container);
    items.forEach((el, index) => {
      window.setTimeout(() => reveal(el), index * GROUP_STAGGER_MS);
    });
  }

  if (prefersReducedMotion() || typeof IntersectionObserver !== 'function' || isDesignMode()) {
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }
      observer.unobserve(entry.target);
      reveal(entry.target);
    });
  }, {
    rootMargin: ROOT_MARGIN,
    threshold: THRESHOLD,
  });

  function prepareGroup(container) {
    if (trackedGroups.has(container)) {
      return;
    }

    const items = collectMedia(container).filter((el) => !shouldSkip(el));
    if (!items.length) {
      trackedGroups.add(container);
      return;
    }

    trackedGroups.add(container);

    if (isInFirstViewport(container)) {
      items.forEach((el) => tracked.add(el));
      return;
    }

    items.forEach((el) => {
      tracked.add(el);
      el.classList.add(PENDING_CLASS);
    });
    groupItems.set(container, items);

    const groupObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        groupObserver.unobserve(entry.target);
        revealGroup(entry.target);
      });
    }, {
      rootMargin: ROOT_MARGIN,
      threshold: 0.12,
    });
    groupObserver.observe(container);
  }

  function prepareClone(el) {
    tracked.add(el);
    const group = el.closest(GROUP_SELECTOR);
    if (group && revealedGroups.has(group)) {
      reveal(el);
      return;
    }
    if (group && groupItems.has(group)) {
      groupItems.get(group).push(el);
      return;
    }
    reveal(el);
  }

  function prepare(el) {
    if (el.classList.contains(PENDING_CLASS) && !tracked.has(el)) {
      prepareClone(el);
      return;
    }
    if (shouldSkip(el) || isInFirstViewport(el)) {
      return;
    }
    tracked.add(el);
    el.classList.add(PENDING_CLASS);
    observer.observe(el);
  }

  function scan(root = document) {
    collectGroups(root).forEach(prepareGroup);
    collectMedia(root).forEach(prepare);
  }

  function init() {
    scan(document.getElementById('main') || document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
  });

  const pendingRoots = new Set();
  let scanScheduled = false;

  const flushScan = () => {
    scanScheduled = false;
    pendingRoots.forEach((node) => {
      if (node.isConnected) {
        scan(node);
      }
    });
    pendingRoots.clear();
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }
        if (
          node.matches?.(MEDIA_SELECTOR)
          || node.matches?.(GROUP_SELECTOR)
          || node.querySelector?.(MEDIA_SELECTOR)
          || node.querySelector?.(GROUP_SELECTOR)
        ) {
          pendingRoots.add(node);
        }
      });
    });
    if (pendingRoots.size && !scanScheduled) {
      scanScheduled = true;
      window.requestAnimationFrame(flushScan);
    }
  });
  mutationObserver.observe(document.getElementById('main') || document.body, {
    childList: true,
    subtree: true,
  });

  const onMotionChange = (event) => {
    if (!event.matches) {
      return;
    }
    document.querySelectorAll(`.${PENDING_CLASS}`).forEach((el) => {
      reveal(el);
      observer.unobserve(el);
    });
  };
  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionChange);
  } else if (typeof motionQuery.addListener === 'function') {
    motionQuery.addListener(onMotionChange);
  }
})();
